// Plays the sound of a moment. A browser gives no sound before the first
// touch or key, so the player makes its audio context then. A sound asked for
// before that is dropped. It does not wait and play late.

import { SOUND_LATE_LIMIT_MS, SOUND_MASTER_LEVEL, SOUND_SAMPLE_RATE } from '../constants';
import { settingsStore } from '../state/settings';
import { type SoundId, soundSamples, soundVariant } from './moments';

const UNLOCK_EVENTS = ['pointerdown', 'keydown'] as const;
const MS_PER_SECOND = 1000;

let context: AudioContext | null = null;
let master: GainNode | null = null;
/** The buffer of each sound that played, so its samples are made once. */
const buffers = new Map<string, AudioBuffer>();

const unlock = (): void => {
  if (context === null) {
    context = new AudioContext();
    master = context.createGain();
    master.gain.value = SOUND_MASTER_LEVEL;
    master.connect(context.destination);
  }
  // A phone suspends the context when the page goes to the back.
  if (context.state !== 'running') void context.resume();
};

const bufferOf = (audio: AudioContext, id: SoundId, variant: number): AudioBuffer => {
  const key = `${id}/${variant}`;
  let buffer = buffers.get(key);
  if (!buffer) {
    const samples = soundSamples(id, variant);
    buffer = audio.createBuffer(1, samples.length, SOUND_SAMPLE_RATE);
    buffer.getChannelData(0).set(samples);
    buffers.set(key, buffer);
  }
  return buffer;
};

/** Call once at startup. */
export const startSoundPlayer = (): void => {
  for (const event of UNLOCK_EVENTS) window.addEventListener(event, unlock, { capture: true });
};

/**
 * Plays a moment after `delayMs`. `count` picks the form of a moment that has
 * more than one: the forms come in turn as the count goes up.
 */
export const playSound = (id: SoundId, delayMs = 0, count = 0): void => {
  if (!settingsStore.get().sound || context === null || master === null) return;
  const audio = context;
  const output = master;
  const start = (): void => {
    const source = audio.createBufferSource();
    source.buffer = bufferOf(audio, id, soundVariant(id, count));
    source.connect(output);
    source.start(audio.currentTime + Math.max(0, delayMs) / MS_PER_SECOND);
  };
  if (audio.state === 'running') {
    start();
    return;
  }
  // A new context takes a moment to start. A sound that waits longer than the limit is dropped.
  const askedAt = performance.now();
  void audio.resume().then(() => {
    if (performance.now() - askedAt < SOUND_LATE_LIMIT_MS) start();
  });
};
